package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** The exact canonical source a caller saw. An unknown basis is represented by null on reads. */
@Serializable
data class SourceViewDto(
    val sourceRevisionId: String,
    val keyId: String,
    val sourceLocale: String,
    val sourceVersion: Int,
    val isPlural: Boolean,
)

/** Full, untrimmed text; immutable on the server for the lifetime of the key. */
@Serializable
data class SourceSnapshotDto(
    val view: SourceViewDto,
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
)

@Serializable
data class SourceChangeDto(
    val keyId: String,
    val locale: String,
    val currentSource: SourceSnapshotDto?,
    val earlierSource: SourceSnapshotDto?,
    val target: TranslationDto?,
)

@Serializable
data class SourceTranslationRequest(
    val sourceView: SourceViewDto,
    /** Required, even when null: null means the caller saw no target row. */
    val expectedTargetRevision: String?,
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
    val state: TranslationState? = null,
)

@Serializable
data class ReaffirmTranslationRequest(
    val sourceView: SourceViewDto,
    val expectedTargetRevision: String,
)

@Serializable
data class SourceReviewDecisionRequest(
    val sourceView: SourceViewDto,
    val expectedTargetRevision: String,
    val action: ReviewAction,
    val note: String? = null,
)

@Serializable
data class SourceReviewBatchItem(
    val keyId: String,
    val sourceView: SourceViewDto,
    val expectedTargetRevision: String,
)

@Serializable
data class SourceReviewBatchRequest(
    val items: List<SourceReviewBatchItem>,
    val action: ReviewAction,
    val note: String? = null,
    val dryRun: Boolean = false,
)

/** An explicit source claim resolves its stable key UUID within the authorized project. */
@Serializable
data class SourceProposalRequest(
    val sourceView: SourceViewDto,
    val locale: String,
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
    val rationale: String? = null,
    val confidence: String? = null,
    val runId: String? = null,
)

private val sourceReviewIdRegex = Regex(
    "^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}${'$'}",
)

fun isCanonicalSourceReviewId(value: String): Boolean = sourceReviewIdRegex.matches(value)

fun validateSourceView(view: SourceViewDto): String? = when {
    !isCanonicalSourceReviewId(view.sourceRevisionId) -> "Source revision must be a canonical UUID."
    !isCanonicalSourceReviewId(view.keyId) -> "Key ID must be a canonical UUID."
    validateLocale(view.sourceLocale) != null -> "Source locale must be a valid BCP-47 locale."
    view.sourceVersion < 0 -> "Source version must be a nonnegative integer."
    else -> null
}

/** Read metadata must agree with its content shape before a client may claim to have seen it. */
fun validateSourceSnapshot(snapshot: SourceSnapshotDto): String? = when {
    validateSourceView(snapshot.view) != null -> validateSourceView(snapshot.view)
    snapshot.view.isPlural && snapshot.value != null -> "A plural source cannot contain scalar text."
    !snapshot.view.isPlural && snapshot.pluralForms != null -> "A scalar source cannot contain plural forms."
    else -> null
}
